import { RAD, DEG, angDiff } from './constants.js';
import { camGeom, cameraViews, inCameraFrame, pxAt } from './optics.js';
import { blocked, insideSite } from './geometry.js';
import { sceneWalls, openingCentre } from './siteGeometry.js';
import { threshold, needsFace } from './standards.js';
import { recordedMotion } from './projection.js';
import { recordingFor } from './recording.js';

/** Small oriented target surface; centre and eye/plate region must be visible.
 * Nine probes make partial obstruction explicit without treating target props
 * as real people or guessing image-processing performance. */
export function inspectSurface(S, cam, p, options = {}, g = camGeom(S, cam)) {
  if (!options._single && g?.m.details?.sensorModules?.length) return cameraViews(S, cam)
    .map(v => ({ ...inspectSurface(S, v.cam, p, { ...options, _single: true }, v.g), moduleId: v.module?.id }))
    .sort((a, b) => Number(b.met) - Number(a.met) || b.visible - a.visible || b.density - a.density)[0];
  if (!g) return { met: false, reason: 'Missing model specifications', visible: 0, total: 9, density: 0 };
  const req = options.req || p.req || 'identify', isPlate = req === 'plate';
  const h = options.height ?? p.height ?? (isPlate ? S.opt.hPlate : S.opt.hFace);
  const heading = options.heading ?? p.heading ?? 0;
  const width = options.width ?? (isPlate ? 0.52 : 0.16), height = options.targetHeight ?? (isPlate ? 0.11 : 0.22);
  const yaw = angDiff(Math.atan2(cam.y - p.y, cam.x - p.x) * DEG, heading);
  const distance = Math.hypot(cam.x - p.x, cam.y - p.y, cam.h - h);
  const downAngle = Math.atan2(Math.abs(cam.h - h), Math.max(0.01, Math.hypot(cam.x - p.x, cam.y - p.y))) * DEG;
  const walls = options.walls || sceneWalls(S);
  const samples = [];
  for (const v of [-0.4, 0, 0.4]) for (const u of [-0.4, 0, 0.4]) {
    const x = p.x - Math.sin(heading * RAD) * width * u, y = p.y + Math.cos(heading * RAD) * width * u, z = h + v * height;
    const framed = inCameraFrame(cam, g, x, y, z);
    const clear = framed && !blocked(cam.x, cam.y, cam.h, x, y, z, walls);
    samples.push({ x, y, height: z, framed, clear, essential: u === 0 || v === 0 });
  }
  // Conservative projected horizontal detail for an oriented surface.
  const density = pxAt(g, distance) * Math.max(0, Math.cos(yaw * RAD));
  const visible = samples.filter(s => s.clear).length;
  const enough = visible / samples.length >= (options.minVisible ?? 8 / 9) && samples.filter(s => s.essential).every(s => s.clear);
  let reason = 'Requirement met';
  if (!samples.some(s => s.framed)) reason = 'Outside camera frame';
  else if (!enough) reason = `Target partly blocked or clipped (${visible}/${samples.length} surface probes visible)`;
  else if (yaw > (options.maxYaw ?? (isPlate ? S.opt.aPlateI : S.opt.faceYaw ?? 45))) reason = `Target faces away or sideways (${yaw.toFixed(1)}°)`;
  else if ((isPlate || needsFace(req)) && downAngle > (options.maxDown ?? (isPlate ? S.opt.aPlateT : S.opt.aFace))) reason = `Viewing angle too steep (${downAngle.toFixed(1)}°)`;
  else if (density < (options.minPx ?? threshold(S, req))) reason = 'Insufficient projected detail';
  const exposure = options.exposureMs ?? recordingFor(S, cam).exposureMs;
  const speed = options.speed ?? 0;
  const blurPx = recordedMotion(cam, g, samples, speed, options.travelHeading ?? cam.pan + 90, exposure);
  if (reason === 'Requirement met' && options.maxBlurPx > 0 && (blurPx == null || blurPx > options.maxBlurPx)) reason = blurPx == null ? exposure > 0 ? 'Motion projection unavailable for this view' : 'Exposure unknown for motion check' : `Motion blur exceeds allowance (${blurPx.toFixed(1)} px)`;
  return { met: reason === 'Requirement met', reason, visible, total: samples.length, density, distance, downAngle, yaw, samples, blurPx };
}

export function taskDemands(S) {
  const out = [];
  for (const task of S.tasks || []) {
    if (task.enabled === false) continue;
    const opening = (S.openings || []).find(o => o.id === task.openingId);
    const centre = opening && openingCentre(S, opening);
    const directions = task.direction === 'both' ? [1, -1] : [task.direction === 'reverse' ? -1 : 1];
    for (const sign of directions) {
      const heading = ((centre?.heading ?? task.heading ?? 0) + (sign === -1 ? 180 : 0)) % 360;
      const nx = Math.cos(heading * RAD), ny = Math.sin(heading * RAD);
      const start = centre ? { x: centre.x + nx * (task.offset ?? 0.7), y: centre.y + ny * (task.offset ?? 0.7) } : { x: task.x ?? 0, y: task.y ?? 0 };
      const length = task.length ?? 1, width = centre ? Math.min(opening.width * 0.65, task.width ?? opening.width) : task.width ?? 0;
      const across = width > 0.2 ? [-0.5, 0, 0.5] : [0];
      for (const [lane, side] of across.entries()) {
        const points = Array.from({ length: length > 0 ? 7 : 1 }, (_, i) => ({
          x: start.x - ny * width * side + nx * length * i / 6,
          y: start.y + nx * width * side + ny * length * i / 6,
          heading, height: task.height ?? (task.req === 'plate' ? S.opt.hPlate : S.opt.hFace)
        }));
        out.push({ ...task, id: `${task.id}:${sign}:${lane}`, taskId: task.id, entryId: task.id,
          name: task.name, x: points[0].x, y: points[0].y, heading, points, required: task.views || 1,
          weight: 1 / (directions.length * across.length), lane, valid: !task.openingId || !!centre,
          length, sign });
      }
    }
  }
  return out;
}

export function assessDemand(S, cam, demand, g = camGeom(S, cam), walls = sceneWalls(S)) {
  if (!demand.valid) return { met: false, reason: 'The associated opening is missing', duration: 0, results: [] };
  // An uninterrupted capture must come from a single recorded sensor stream.
  if (g?.m.details?.sensorModules?.length) return cameraViews(S, cam).map(v => ({
    ...assessDemand(S, v.cam, demand, v.g, walls), moduleId: v.module?.id
  })).sort((a, b) => Number(b.met) - Number(a.met) || b.duration - a.duration)[0];
  const results = demand.points.map(p => insideSite(S, p.x, p.y)
    ? inspectSurface(S, cam, p, { ...demand, height: p.height, travelHeading: p.heading, width: demand.targetWidth, targetHeight: demand.targetHeight, walls }, g)
    : { met: false, reason: 'Capture path lies outside usable site', density: 0 });
  let run = 0, longest = 0;
  for (const r of results) { run = r.met ? run + 1 : 0; longest = Math.max(longest, run); }
  const speed = demand.speed ?? 1.4, dt = demand.points.length > 1 ? demand.length / (demand.points.length - 1) / Math.max(0.01, speed) : 0;
  const duration = Math.max(0, longest - 1) * dt;
  const minDuration = demand.minDuration ?? 0;
  const met = minDuration > 0 ? duration + 1e-9 >= minDuration : longest > 0;
  return { met, duration, results, minPx: Math.min(...results.filter(r => r.met).map(r => r.density), Infinity),
    reason: met ? 'Requirement met' : results.find(r => !r.met)?.reason || 'Capture interval too short' };
}

export function assessTasks(S) {
  const demands = taskDemands(S), walls = sceneWalls(S);
  const cameraGeometries = S.cams.map(cam => ({ cam, g: camGeom(S, cam) }));
  return (S.tasks || []).filter(t => t.enabled !== false).map(task => {
    const lanes = demands.filter(d => d.taskId === task.id).map(d => {
      const checks = cameraGeometries.map(({ cam, g }) => ({ cameraId: cam.id, cameraName: cam.name, ...assessDemand(S, cam, d, g, walls) }));
      const cameras = checks.filter(c => c.met).map(c => c.cameraId);
      return { ...d, checks, cameras, complete: cameras.length >= d.required };
    });
    return { task, lanes, met: lanes.filter(l => l.complete).length, total: lanes.length, complete: lanes.length > 0 && lanes.every(l => l.complete) };
  });
}
